/* eslint-disable no-unused-vars */
/* eslint-disable react/no-array-index-key */
import React, { useMemo, useCallback, useState } from "react";
import { Tooltip, IconButton } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useDispatch,useSelector } from "react-redux";
import Table from "../../../components/Table/Table";
import { Header } from "../../../components";
import { EVerificationBaseColumns } from "../../../utils/utils";
import TicketButton from "../../../components/TableComponents/TicketButton/TicketButton";
import ActionButtons from "../../../components/TableComponents/ActionButtons/ActionButtons";
import TableLayout from "../../../layouts/TableLayout/TableLayout";
import useTicketFilters from "../../../utils/ticketFilters";
import useTicketData from "../../../hooks/useTicketData";
import { API } from "../../../apis/api";
import ConfirmEVerificationModal from "../../../components/ConfirmEVerificationModal/ConfirmEVerificationModal";
import DocumentViewerModal from "../../../components/DocumentViewerModal/DocumentViewerModal";
import useDocumentViewer from "../../../hooks/useDocumentViewer";
import useTicketExport from "../../../hooks/useTicketExport";
import NoItemsFound from "../../../components/NoItemsFound/NoItemsFound";
import {
  errorSnackbar,
  successSnackbar,
} from "../../../redux/features/customSnackbar/action";

const API_BASE_URL = "/document/get-download-url/";
function EVerification() {
  const [openModal, setOpenModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");
  const dispatch = useDispatch();

  const userId = useSelector((state)=>state.auth.user.userId)
  // Custom hooks
  const {
    searchQuery,
    allRows: filteredRows,
    loading,
    isRefreshing,
    handleRefresh,
    handleSearchQuery,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTicketData("SRN_UPDATE", true);

  const {
    documentViewerModal,
    handleDownloadFile,
    handleViewDocument,
    handleCloseDocumentViewer,
  } = useDocumentViewer();

  const getDownloadUrl = async (ticketId) => {
    try {
      const response = await API.post(API_BASE_URL, {
        ticket_id: ticketId,
        sub:userId,
        document_type: "entitlement_letter",
      });

      return response.data?.download_url || null;
    } catch (err) {
      console.log("Download url failed", err);
      return null;
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // Handle verify API and remove ticket from UI
  const handleVerifyTicket = async (ticketNumber, ticketId) => {
    try {
      const payload = {
        ticket_code: ticketNumber,
        ticket_status: "E_VERIFICATION",
        sub:userId,
      };

      const response = await API.post(`ticket/update-ticket-status/`, payload);
      if (response) {
        await handleRefresh();
        dispatch(
          successSnackbar({
            title: "Verify EL Successfully",
            subtitle: `Your EL Verify successfully of ticket no : ${ticketNumber}`,
          })
        );
      }
    } catch (err) {
      console.error(`Error verifying ticket ${ticketNumber}:`, err);
      dispatch(
        errorSnackbar({
          title: "Error",
          subtitle: err.data?.message || "Failed to verify ticket",
        })
      );
    }
  };

  // Handle action button clicks
  const handleActionClick = useCallback((action, rowData) => {
    if (action === "Verify") {
      setSelectedRow(rowData);
      handleOpenModal();
    }

    if (action === "Update EL") {
      console.log(`Updating EL for ticket: ${rowData.ticketNumber}`);
    }
  }, []);

  const columns = useMemo(() => {
    const updatedColumns = [...EVerificationBaseColumns];

    updatedColumns[0] = {
      width: 180,
      title: "Ticket Number",
      field: "ticketNumber",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <TicketButton
          ticketNumber={rowData.ticketNumber}
          ticketId={rowData.id}
        />
      ),
    };

    updatedColumns[3] = {
      width: 280,
      title: "Entitlement Letter",
      field: "entitlementLetter",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.1rem" }}>
          <span style={{ marginRight: "0.1rem" }}>{rowData.document}</span>

          <Tooltip title="Download">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                handleDownloadFile(rowData.ticketNumber);
              }}
            >
              <GetAppIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="View">
            <IconButton
              size="small"
              color="primary"
              onClick={() => {
                handleViewDocument(rowData.ticketNumber);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
      ),
    };

    updatedColumns[5] = {
      width: 180,
      title: "Action",
      field: "action",
      enableSearch: false,
      enableFilter: false,
      render: (rowData) => (
        <ActionButtons
          actions={["Verify"]}
          status={rowData.entitlementLetter ? "Verified" : "Pending"}
          onActionClick={(action) => handleActionClick(action, rowData)}
        />
      ),
    };

    return updatedColumns;
  }, [handleActionClick, handleDownloadFile, handleViewDocument]);

  const { handleExport, isExporting } = useTicketExport("SRN_UPDATE", columns);

  // Render the table
  return (
    <TableLayout
      styles={{ tableInnerWrapper: { paddingBottom: "0rem" } }}

      header={
        <Header
          searchQuery={searchQuery}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
          handleSearchQuery={handleSearchQuery}
          onExport={handleExport}
          isExporting={isExporting}
          disableExport={filteredRows.length === 0}
        />
      }
    >
      {filteredRows.length === 0 && !(loading) ? (
        <NoItemsFound />
      ) : (
        <Table
          columns={columns}
          defaultColumns={columns}
          rows={filteredRows}
          hideCheckbox
          rowHeight={45}
          setHasActiveEdits={() => {}}
          isDataLoading={loading || isRefreshing}
          serverSide
          totalRows={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
      <ConfirmEVerificationModal
        open={openModal}
        onClose={handleCloseModal}
        handleVerifyTicket={handleVerifyTicket}
        selectedRow={selectedRow}
      />
      <DocumentViewerModal
        open={documentViewerModal.open}
        onClose={handleCloseDocumentViewer}
        documentUrl={documentViewerModal.documentUrl}
        documentName={documentViewerModal.documentName}
        documentType={documentViewerModal.documentType}
        isLoading={documentViewerModal.isLoading}
        error={documentViewerModal.error}
      />
    </TableLayout>
  );
}

EVerification.propTypes = {};

export default EVerification;
